import {
  BusinessNumberOutSchema,
  ReceiverLineOutSchema,
  ReceiverNumberMinimalOutSchema,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import { getBusinessNumbers } from "@/api/requests/numbers";
import { getReceiverLineList } from "@/api/requests/receiverLine";
import {
  getTeamMemberReceiverNumbers,
  getTeamMemberV2,
} from "@/api/requests/teamMembers";
import {
  INTEGRATION_PLANS_IDS,
  PINIA_PERSIST_OPTIONS,
} from "@/helpers/constants";
import { generateKeyPair } from "@/helpers/encryptionKeys";
import logger from "@/helpers/logger";
import router from "@/router";
import { notify } from "@kyvg/vue3-notification";
import { defineStore } from "pinia";
import { useAuthStore } from "./auth.store";
import { useBusinessStore } from "./businesses.store";
import { useUsersStore } from "./users.store";

export type AllBusinessNumbers = BusinessNumberOutSchema;

export type ReceiverNumber = {
  line: ReceiverLineOutSchema | undefined;
  business_number: AllBusinessNumbers | undefined;
  id: number | null | undefined;
  extension_code: number;
};

interface NumberState {
  numbers?: ReceiverNumber[] | null; // replace type later
  activeNumber?: ReceiverNumber | null;
  allBusinessNumbers?: AllBusinessNumbers[];
  activeBusinessNumber?: AllBusinessNumbers | null;
  activeBusinessNumberForReports?: AllBusinessNumbers | null;
  subscriptionStatus?: string | null;
  allNumbersRegisteredUnderABusiness?: AllBusinessNumbers[] | null;
}

export const useNumberStore = defineStore("numbers", {
  state: (): NumberState => ({
    numbers: null, // list all reciever numbers under this agent
    activeNumber: null,
    allBusinessNumbers: [],
    activeBusinessNumber: null,
    activeBusinessNumberForReports: null, // prevent re render everytime we change route
    subscriptionStatus: null,
    allNumbersRegisteredUnderABusiness: null,
  }),
  getters: {
    currentUserReceiverKey(): number | string | undefined | null {
      return this.activeNumber?.id;
    },
    activeBusinessNumberId(): number | undefined | null {
      // active business number id. Don't want to change the name cause it might break alot of things
      if (!this.activeNumber) {
        return undefined;
      }

      return this.activeNumber.business_number?.id;
    },
    activeAllBusinessNumberId(): number | undefined | null {
      // active business number for all numbers id.
      if (!this.activeBusinessNumber) {
        return undefined;
      }

      return this.activeBusinessNumber.id;
    },
    getOrderedBusinessNumbers(): ReceiverNumber[] {
      if (!this.numbers) {
        return [];
      }

      const sortedNumbers = [...this.numbers].sort((a, b) => {
        if (this.activeNumber && a.id === this.activeNumber.id) {
          return -1;
        } else if (this.activeNumber && b.id === this.activeNumber.id) {
          return 1;
        } else {
          return 0;
        }
      });

      return sortedNumbers;
    },
    getInactiveBusinessNumbers(): ReceiverNumber[] {
      return this.getOrderedBusinessNumbers.slice(1);
    },
    showIntegration(): boolean {
      if (this.allBusinessNumbers) {
        // This is the plan id for the plans that are valid for integration
        const response = this?.allBusinessNumbers.find((number) => {
          if (!number.plan) {
            return false;
          }

          return INTEGRATION_PLANS_IDS.includes(number?.plan);
        });

        return !!response;
      }

      return false;
    },
    requiresNINVerification(): boolean | undefined {
      return this.allBusinessNumbers?.some(
        (number) => number.country_code == "NG"
      );
    },
    getAllNumbersRegisteredUnderABusiness(): AllBusinessNumbers[] {
      return this.allNumbersRegisteredUnderABusiness ?? [];
    },
  },
  actions: {
    async getUserPhonesFromBusiness() {
      const userStore = useUsersStore();
      const businessStore = useBusinessStore();
      const authStore = useAuthStore();

      const businessId = businessStore.currentBusinessId;
      const userId = businessStore.currentUserId;

      if (!businessId || !userId) {
        logger.error(
          new Error(`No business or user id found ${businessId}, ${userId}`)
        );

        await authStore.logout();

        return;
      }

      const keypair = await generateKeyPair();

      const publickey = keypair.publicKey;

      const receiverLinePayload = {
        public_key: publickey,
        is_no_trunk_team: false,
      };

      // 1. Returns receiver credentials and business numbers of current user under the current business
      // 2. Returns all businesses owned by owner or manager
      const [
        teamMemberResponse,
        allBusinessNumbersResponse,
        teamMemberReceiverNumbersResponse,
      ] = await Promise.all([
        getTeamMemberV2({ businessId, userId }),
        getBusinessNumbers({ businessId }),
        getTeamMemberReceiverNumbers({ businessId, userId }),
      ]);

      let receiverLineList: ReceiverLineOutSchema[] = [];

      try {
        // This endpoint throws an error for unpaid invoices, don't block login
        const receiverLineListResponse = await getReceiverLineList({
          businessId,
          receiverLineInSchema: receiverLinePayload,
        });

        receiverLineList = receiverLineListResponse.data;
      } catch (error) {
        handleError({ error, silent: true });
      }

      this.allNumbersRegisteredUnderABusiness = allBusinessNumbersResponse.data;

      userStore.permissions = teamMemberResponse.data.permission;

      const allowedBusiness =
        teamMemberResponse.data?.permission?.business_numbers;

      this.allBusinessNumbers = allBusinessNumbersResponse.data.filter(
        (item: AllBusinessNumbers) => {
          if (!item.id) {
            return false;
          }

          return allowedBusiness?.includes(item.id);
        }
      ); // filter only allowed business i.e business granted permissions

      this.activeBusinessNumber = this.allBusinessNumbers
        ? this.allBusinessNumbers[0]
        : null;
      this.activeBusinessNumberForReports = this.activeBusinessNumber;

      const receivers = this.generateReactiveStructure(
        teamMemberReceiverNumbersResponse.data,
        receiverLineList,
        allBusinessNumbersResponse.data
      );
      // store the receivers and business numbers. whether added or owned
      this.numbers = receivers;

      if (this.numbers?.length) {
        if (!this.activeNumber) {
          // sets the first element in the array to the active number if no active number exists
          this.activeNumber = this.numbers?.[0];
        }
      } else {
        this.activeNumber = null;
      }
    },
    setActiveNumber(
      number: ReceiverNumber | undefined,
      refresh: boolean = false
    ) {
      if (!number) {
        notify({
          type: "error",
          duration: 1500,
          title: `Default number switch failed`,
          text: `The selected number does not exist. Please try again.`,
        });

        return;
      }

      this.activeNumber = number;

      notify({
        type: "success",
        duration: 1500,
        title: `Default number switch successful`,
        text: `Your default number has been switched to ${number.business_number?.phone_number}.`,
      });

      if (refresh) {
        router.go(0);
      }
    },
    refreshActiveNumber() {
      // TODO: currentUser has no receivers, confirm why
      // const userStore = useUsersStore();
      // const number = userStore.currentUser?.receivers[0];

      const number = this.numbers?.[0];

      this.setActiveNumber(number, false);
    },

    getBusinessNumberIdByNumber(number?: string) {
      const business = this.allBusinessNumbers?.filter(
        (businessNumber) => businessNumber.phone_number === number
      );

      return business?.[0]?.id;
    },

    generateReactiveStructure(
      teamMemberReceiverNumbersResponse: ReceiverNumberMinimalOutSchema[],
      receiverLineListResponse: ReceiverLineOutSchema[],
      allBusinessNumbersResponse: AllBusinessNumbers[]
    ) {
      const receivers = teamMemberReceiverNumbersResponse;
      const lines = receiverLineListResponse;
      const businessNumbers = allBusinessNumbersResponse;

      return receivers.map((receiver) => {
        // Find the corresponding line for this receiver
        const line = lines.find(
          (l) => Number(l.username) === receiver.extension_code
        );

        // Find the corresponding business number
        const businessNumber = businessNumbers.find(
          (number) => number.id === receiver.business_number
        );

        return {
          line,
          business_number: businessNumber,
          id: receiver.id,
          extension_code: receiver.extension_code,
        };
      });
    },
  },
  persist: PINIA_PERSIST_OPTIONS,
});
